import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { when } from 'mobx';
import { Form, FormControl } from 'react-bootstrap';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
} from '@patternfly/react-core';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import { storesContext } from 'stores';

import './styles.scss';

const MgmtCustomSkillPathsFilters = (props) => {
  const { t } = useTranslation();
  const { onSelectSkillPath, onSearchUsers, enableFilters } = props;
  const { organizationStore } = useContext(storesContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedSkillPathTitle, setSelectedSkillPathTitle] = useState(null);

  const [selectedSkillPathDueDate, setSelectedSkillPathDueDate] =
    useState('Not set');

  const handleSkillPathSelect = (skillPathId) => {
    const _found = skillPathId
      ? organizationStore.customSkillPaths?.find((c) => c.code === skillPathId)
      : null;

    const _dueDate =
      _found && _found.end_date
        ? DateTime.fromISO(_found.end_date).toFormat('yyyy-MM-dd')
        : t('Not set');
    const _skillPathTitle = _found && _found.title ? _found.title : null;
    setSelectedSkillPathDueDate(_dueDate);
    setSelectedSkillPathTitle(_skillPathTitle);

    onSelectSkillPath(skillPathId);
  };

  useEffect(() => {
    when(
      () => organizationStore.organization,
      async () => {
        if (organizationStore.organization.id) {
          await organizationStore.getCustomSkillPaths(
            organizationStore.organization.id,
            true,
          );
          const defaultSelectCSK =
            organizationStore.customSkillPaths?.length > 0
              ? organizationStore.customSkillPaths[0].code
              : null;

          handleSkillPathSelect(defaultSelectCSK);
        }
      },
    );
  }, [organizationStore.organization]);

  const onSearch = (e) => {
    e.persist();
    const { value } = e.target;

    const query = value && value.toLowerCase();
    onSearchUsers(query);
  };

  const renderFilters = () => {
    return (
      <React.Fragment>
        <div className="mgmt-csk-filters">
          <h4>{t('Progress Monitor')}</h4>
          <Form
            horizontal
            onSubmit={(e) => {
              e.preventDefault();
              return false;
            }}
          >
            <FormControl
              data-analytics-id="prog-mon-cust-skillp-mngmt-dboard-lp"
              type="text"
              placeholder={t('Search by name, username, email')}
              onKeyUp={onSearch}
              disabled={
                !enableFilters || !organizationStore.customSkillPathsLoaded
              }
            />
            <div className="mgmt-csk-other-filter">
              <div className="filter-row">
                <div className="col">
                  <span className="span-label">{t('Filters')}:</span>
                </div>
                <div className="col">
                  <Dropdown
                    className="mgmt-csk-filter-dropdown"
                    isOpen={isDropdownOpen}
                    onOpenChange={(open) => setIsDropdownOpen(open)}
                    onSelect={() => setIsDropdownOpen(false)}
                    toggle={(toggleRef) => (
                      <MenuToggle
                        id="mgmt-csk-dropdown"
                        isDisabled={
                          !enableFilters ||
                          !organizationStore.customSkillPathsLoaded
                        }
                        isExpanded={isDropdownOpen}
                        onClick={() => setIsDropdownOpen((prev) => !prev)}
                        ref={toggleRef}
                      >
                        <span className="dropdown-label">
                          {organizationStore.customSkillPathsLoaded
                            ? selectedSkillPathTitle ||
                              t('Filter by Custom Skill Path')
                            : t('Loading Custom Skill Path')}
                        </span>
                      </MenuToggle>
                    )}
                  >
                    <DropdownList>
                      {organizationStore.customSkillPathsLoaded &&
                      organizationStore.customSkillPaths?.length ? (
                        organizationStore.customSkillPaths.map((csk) => (
                          <DropdownItem
                            key={csk.title}
                            onClick={() => handleSkillPathSelect(csk.code)}
                          >
                            {csk.title}
                          </DropdownItem>
                        ))
                      ) : (
                        <DropdownItem isDisabled key="no-csk">
                          {t('No Custom Skill Path')}
                        </DropdownItem>
                      )}
                    </DropdownList>
                  </Dropdown>
                </div>
              </div>
              <div className="filter-row">
                <div className="col">
                  <span className="span-label">{t('Due Date')}:</span>
                </div>
                <div className="col">{selectedSkillPathDueDate}</div>
              </div>
            </div>
          </Form>
        </div>
      </React.Fragment>
    );
  };

  return <React.Fragment>{renderFilters()}</React.Fragment>;
};

MgmtCustomSkillPathsFilters.propTypes = {
  onSelectSkillPath: PropTypes.func,
  onSearchUsers: PropTypes.func,
  enableFilters: PropTypes.bool,
};
MgmtCustomSkillPathsFilters.defaultProps = {
  onSelectSkillPath: () => {},
  onSearchUsers: () => {},
  enableFilters: false,
};

export default observer(MgmtCustomSkillPathsFilters);
